import React, { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { AutoTagsFiltersBar } from "features/tags/filters/AutoTagsFiltersBar"
import { SweepStatusBanner } from "features/tags/SweepStatusBanner"
import { RulesTable } from "features/tags/tables/RulesTable"

import { useDebouncedEffect } from "hooks/useDebouncedEffect"

import { tagsService } from "services/tags/tags.service"
import { TagRule, TagWithUsage } from "services/tags/type"

const DEBOUNCE_MS = 300

type Props = {
  loading: boolean
  setLoading: (v: boolean) => void
  automaticTags: TagWithUsage[]
  onCreate: () => void
  onEdit: (r: TagRule) => void
  onGlobalRefresh: () => Promise<void>
  refreshKey: number
  onRulesLoaded?: (rows: TagRule[]) => void
}

export const AutomaticTagsTab: React.FC<Props> = ({
  loading,
  setLoading,
  refreshKey,
  automaticTags,
  onCreate,
  onEdit,
  onGlobalRefresh,
  onRulesLoaded
}) => {
  const [rows, setRows] = useState<TagRule[]>([])

  // filters
  const [query, setQuery] = useState("")
  const [mode, setMode] = useState<"" | "BATCH" | "REALTIME">("")
  const [onlyActive, setOnlyActive] = useState(true)
  const [tagId, setTagId] = useState("")

  const tagOptions = useMemo(
    () =>
      automaticTags
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((t) => ({ id: t.id, name: t.name })),
    [automaticTags]
  )

  const load = useCallback(async () => {
    try {
      const res = await tagsService.getRules({
        page: 1,
        pageSize: 200,
        sortRow: "priority",
        sortOrder: "desc",
        q: query || undefined,
        isActive: onlyActive ? true : undefined,
        evaluationMode: mode || undefined,
        tagId: tagId || undefined
      })

      const safe = res?.data ?? []
      setRows(safe)
      onRulesLoaded?.(safe)
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to load automatic tag rules")
    }
  }, [query, mode, onlyActive, tagId, onRulesLoaded])

  useDebouncedEffect(
    () => {
      load()
    },
    [load, refreshKey],
    DEBOUNCE_MS
  )

  const onRefreshLocal = useCallback(async () => {
    setLoading(true)
    try {
      await load()
    } finally {
      setLoading(false)
    }
  }, [load, setLoading])

  const onDelete = useCallback(
    async (id: string) => {
      const ok = window.confirm("Delete rule?")
      if (!ok) return

      setLoading(true)
      try {
        const res = await tagsService.deleteRule(id)
        if (res?.deleted) {
          const unassigned = res.unassigned ?? 0
          toast.success(
            unassigned > 0 ? `Rule deleted and its tag removed from ${unassigned} player${unassigned === 1 ? "" : "s"}` : "Rule deleted"
          )
          await onGlobalRefresh()
        }
      } catch (e: any) {
        toast.error(e?.data?.message ?? e?.message ?? "Failed to delete rule")
      } finally {
        setLoading(false)
      }
    },
    [onGlobalRefresh, setLoading]
  )

  // Both run endpoints start a background sweep over ALL users and return immediately;
  // progress and final counts appear in the Runs History tab.
  const onRunRule = useCallback(async (id: string) => {
    const toastId = toast.loading("Starting rule run...")
    try {
      const res = await tagsService.runRule(id)
      if (!res) throw new Error("No response")
      toast.update(toastId, {
        render: "Rule run started. Progress appears in Runs History.",
        type: "success",
        isLoading: false,
        autoClose: 4000
      })
    } catch (e: any) {
      toast.update(toastId, {
        render: e?.data?.message ?? e?.message ?? "Failed to start rule run",
        type: "error",
        isLoading: false,
        autoClose: 6000
      })
    }
  }, [])

  const onRunBatch = useCallback(async () => {
    const toastId = toast.loading("Starting batch run...")
    try {
      const res = await tagsService.runBatch()
      if (!res) throw new Error("No response")
      toast.update(toastId, {
        render: "Batch run started. Progress appears in Runs History.",
        type: "success",
        isLoading: false,
        autoClose: 4000
      })
    } catch (e: any) {
      toast.update(toastId, {
        render: e?.data?.message ?? e?.message ?? "Failed to start batch run",
        type: "error",
        isLoading: false,
        autoClose: 6000
      })
    }
  }, [])

  return (
    <>
      <SweepStatusBanner />

      <AutoTagsFiltersBar
        loading={loading}
        query={query}
        onQuery={setQuery}
        mode={mode}
        onMode={setMode}
        onlyActive={onlyActive}
        onOnlyActive={setOnlyActive}
        tagId={tagId}
        onTagId={setTagId}
        tagOptions={tagOptions}
        onRefresh={onRefreshLocal}
        onRunBatch={onRunBatch}
        onCreate={onCreate}
      />

      <RulesTable rows={rows} loading={loading} onEdit={onEdit} onDelete={onDelete} onRun={onRunRule} />
    </>
  )
}
